import type { ConnectionState, StatusOverlayProps } from "../types";

/** Color mapping for each connection state. */
const stateColors: Record<ConnectionState, string> = {
  connecting: "#f59e0b",
  connected: "#22c55e",
  disconnected: "#ef4444",
};

/** Compute the user-facing label based on state and app readiness. */
function getLabel(state: ConnectionState, appReady: boolean): string {
  if (state === "connecting") return "Starting app…";
  if (state === "connected") return appReady ? "Ready" : "Starting app…";
  return "Disconnected";
}

/**
 * Status overlay for the stream player.
 *
 * Displays connection state indicator and live stream statistics
 * (FPS, bitrate, latency, resolution) when visible and connected.
 */
export default function StatusOverlay({
  connectionState,
  stats,
  visible,
  appReady = false,
}: StatusOverlayProps) {
  if (!visible) return null;

  const color = stateColors[connectionState];
  const label = getLabel(connectionState, appReady);

  return (
    <div
      style={{
        position: "absolute",
        top: 8,
        left: 8,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {/* Connection state indicator */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "rgba(0, 0, 0, 0.7)",
          borderRadius: 4,
          padding: "4px 8px",
          fontSize: 12,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
            display: "inline-block",
            boxShadow: connectionState === "connected"
              ? `0 0 6px ${color}`
              : "none",
          }}
        />
        <span style={{ color: "#ccc" }}>{label}</span>
      </div>

      {/* Stream statistics (only when connected) */}
      {connectionState === "connected" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto",
            gap: "2px 12px",
            background: "rgba(0, 0, 0, 0.7)",
            borderRadius: 4,
            padding: "4px 8px",
            fontSize: 11,
            fontFamily: "monospace",
          }}
        >
          <StatRow label="FPS" value={stats.fps} unit="" />
          <StatRow label="Bitrate" value={stats.bitrate_kbps} unit=" kbps" />
          <StatRow label="Latency" value={stats.latency_ms} unit=" ms" />
          <StatRow label="Res" value={null} text={stats.resolution} />
        </div>
      )}
    </div>
  );
}

/** Single stat row in the overlay grid. */
function StatRow({
  label,
  value,
  unit = "",
  text,
}: {
  label: string;
  value: number | null;
  unit?: string;
  text?: string | null;
}) {
  const display = text ?? (value != null ? `${value}${unit}` : "--");

  return (
    <>
      <span style={{ color: "#888" }}>{label}</span>
      <span style={{ color: "#e0e0e0", textAlign: "right" }}>{display}</span>
    </>
  );
}
